import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api, { blogsAPI, generationAPI } from '../services/api';
import { ASPECT_RATIOS, DEFAULT_ASPECT_RATIO } from '../constants/aspectRatios';
import ArticleEditorModal from '../components/ArticleEditorModal';
import SnakeGame from '../components/SnakeGame';
import TetrisGame from '../components/TetrisGame';
import './BlogDashboard.css';

// Memoize SnakeGame to prevent crashes/re-renders on parent updates
const SnakeGameMemo = React.memo(SnakeGame);
const TetrisGameMemo = React.memo(TetrisGame);

// ── Helpers ────────────────────────────────────────────────

const DEFAULT_GS = {
  num_blocks: 4,
  word_count_target: 1200,
  num_images: 4,
  num_real_photos: 0,
  ai_provider: 'gemini',
  aspect_ratio: '16:9',
};

function getGs(blog) {
  return blog.generation_settings || DEFAULT_GS;
}

function gsSummary(blog) {
  const gs = getGs(blog);
  const provider = (gs.ai_provider || 'gemini').charAt(0).toUpperCase() + (gs.ai_provider || 'gemini').slice(1);
  return `${gs.num_blocks ?? 4} blocks · ${gs.word_count_target ?? 1200} words · ${provider}`;
}

function initials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');
}

function emptySettings() {
  return { topic: '', promos: [''] };
}

// ── BlogDashboard (page) ───────────────────────────────────

function BlogDashboard() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openPanelId, setOpenPanelId] = useState(null);
  const [generatingMap, setGeneratingMap] = useState({});
  // readyMap: { [blogId]: articleId } — set only when generated THIS session or from backend last_article_id
  const [readyMap, setReadyMap] = useState({});
  const [compactSettings, setCompactSettings] = useState({});
  const [editorArticleId, setEditorArticleId] = useState(null);
  const [editorAspectRatio, setEditorAspectRatio] = useState('16:9');
  const [snakeScore, setSnakeScore] = useState(null);
  const [tetrisScore, setTetrisScore] = useState(null);
  const snakeActiveRef = useRef(false);

  const loadBlogs = useCallback(async () => {
    try {
      const res = await blogsAPI.getAll();
      const data = res.data || [];
      setBlogs(data);
      setReadyMap((prev) => {
        const next = { ...prev };
        data.forEach((blog) => {
          if (blog.last_article_id && blog.last_article_is_new && !next[blog.id]) {
            next[blog.id] = blog.last_article_id;
          }
        });
        return next;
      });
    } catch (err) {
      setError('Failed to load blogs.');
    } finally {
      setLoading(false);
    }
  }, []);

  const location = useLocation();
  useEffect(() => {
    loadBlogs();
  }, [loadBlogs, location.pathname]);

  const togglePanel = useCallback((blogId) => {
    setOpenPanelId((prev) => {
      const opening = prev !== blogId;
      if (opening) {
        setCompactSettings((s) => ({ ...s, [blogId]: emptySettings() }));
        // Re-fetch blogs to get latest generation_settings when opening a panel
        blogsAPI.getAll().then((res) => {
          setBlogs(res.data || []);
        }).catch(() => {});
      }
      return opening ? blogId : null;
    });
  }, []);

  const closePanel = useCallback((blogId) => {
    setOpenPanelId(null);
    setCompactSettings((s) => ({ ...s, [blogId]: emptySettings() }));
  }, []);

  const updateSetting = useCallback((blogId, key, value) => {
    setCompactSettings((s) => ({
      ...s,
      [blogId]: { ...(s[blogId] || emptySettings()), [key]: value },
    }));
  }, []);

  // Poll generating articles every 5s
  useEffect(() => {
    const terminalStatuses = new Set(['ready', 'error']);
    const activeEntries = Object.entries(generatingMap).filter(
      ([, val]) => val && !terminalStatuses.has(val.status)
    );

    if (activeEntries.length === 0) return;

    const intervals = activeEntries.map(([blogId, { articleId, startedAt }]) => {
      const id = setInterval(async () => {
        try {
          if (Date.now() - startedAt > 900000) {
            clearInterval(id);
            setGeneratingMap((m) => ({
              ...m,
              [blogId]: { ...m[blogId], status: 'error', errorMsg: 'Generation timed out after 15 minutes.' },
            }));
            return;
          }

          const res = await generationAPI.getArticle(articleId);
          const article = res.data?.article ?? res.data;
          const artStatus = article?.status;

          if (artStatus === 'completed') {
            clearInterval(id);
            setGeneratingMap((m) => ({ ...m, [blogId]: { ...m[blogId], status: 'ready' } }));
            setReadyMap((r) => ({ ...r, [blogId]: articleId }));
          } else if (artStatus === 'awaiting_news_selection') {
            const allIdx = Array.from({ length: 100 }, (_, i) => i);
            generationAPI.confirmNews(articleId, { selected_indices: allIdx }).catch(() => {});
          } else if (artStatus === 'failed') {
            clearInterval(id);
            const msg = article?.status_message || 'Generation failed.';
            setGeneratingMap((m) => ({
              ...m,
              [blogId]: { ...m[blogId], status: 'error', errorMsg: msg },
            }));
          }
        } catch {
          // Network error — keep polling
        }
      }, 5000);
      return { blogId, id };
    });

    return () => {
      intervals.forEach(({ id }) => clearInterval(id));
    };
  }, [generatingMap]);

  const handleGenerated = useCallback((blogId, articleId) => {
    setGeneratingMap((m) => ({ ...m, [blogId]: { articleId, status: 'queued', startedAt: Date.now() } }));
    setOpenPanelId(null);
    setCompactSettings((s) => ({ ...s, [blogId]: emptySettings() }));
  }, []);

  const dismissReady = useCallback((blogId) => {
    setReadyMap((r) => { const next = { ...r }; delete next[blogId]; return next; });
    setGeneratingMap((m) => { const next = { ...m }; delete next[blogId]; return next; });
  }, []);

  const handleRatioChange = useCallback((blogId, newRatio) => {
    setBlogs((prev) => prev.map((b) => {
      if (b.id !== blogId) return b;
      const gs = { ...(b.generation_settings || DEFAULT_GS), aspect_ratio: newRatio };
      return { ...b, generation_settings: gs };
    }));
    blogsAPI.getOne(blogId).then((res) => {
      const current = res.data.generation_settings || {};
      api.patch(`/api/blogs/${blogId}`, {
        generation_settings: { ...current, aspect_ratio: newRatio }
      }).catch((err) => console.error('Failed to save aspect ratio:', err));
    }).catch(() => {
      api.patch(`/api/blogs/${blogId}`, {
        generation_settings: { ...DEFAULT_GS, aspect_ratio: newRatio }
      }).catch((err) => console.error('Failed to save aspect ratio:', err));
    });
  }, []);

  const openEditor = useCallback((articleId, blogId) => {
    setEditorArticleId(articleId);
    if (blogId) {
      const targetBlog = blogs.find((b) => b.id === blogId);
      const ratio = targetBlog?.generation_settings?.aspect_ratio || '16:9';
      setEditorAspectRatio(ratio);
      setReadyMap((r) => { const next = { ...r }; delete next[blogId]; return next; });
    }
    generationAPI.markViewed(articleId).catch(() => {});
  }, [blogs]);

  const anyGenerating = Object.values(generatingMap).some(
    (g) => g && !['ready', 'error'].includes(g.status)
  );

  const handleSnakeScore = useCallback(async (score) => {
    setSnakeScore(score);
    try {
      await fetch('/api/snake/scores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ score }),
      });
    } catch { /* ignore */ }
  }, []);

  const handleTetrisScore = useCallback(async (score, metadata = {}) => {
    setTetrisScore(score);
    try {
      await api.post('/api/game/scores', {
        game_type: 'tetris',
        score,
        level: metadata.level || null,
        lines_cleared: metadata.lines || null,
      });
    } catch {}
  }, []);

  if (loading) {
    return (
      <div className="blog-dashboard-loading">
        <div className="spinner" style={{ width: 36, height: 36, border: '3px solid var(--color-border)', borderTopColor: 'var(--color-accent-primary)', borderRadius: '50%' }} />
        <span>Loading blogs…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-dashboard">
        <div className="blog-dashboard-empty">
          <span className="blog-dashboard-empty-icon">⚠️</span>
          <h3>Something went wrong</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-dashboard">
      <div className="blog-dashboard-header">
        <h1 className="blog-dashboard-title gradient-text">Blogs</h1>
        <p className="blog-dashboard-subtitle">Select a blog and generate a new article.</p>
      </div>

      {blogs.length === 0 ? (
        <div className="blog-dashboard-empty">
          <span className="blog-dashboard-empty-icon">📝</span>
          <h3>No blogs yet</h3>
          <p>Create your first blog in the Blog Writer to get started.</p>
          <Link to="/blog-writer" className="btn-primary">Open Blog Writer</Link>
        </div>
      ) : (
        <>
          <div className="blog-card-grid">
            {blogs.map((blog) => (
              <BlogCard
                key={blog.id}
                blog={blog}
                panelOpen={openPanelId === blog.id}
                settings={compactSettings[blog.id] || emptySettings()}
                generating={generatingMap[blog.id] || null}
                readyArticleId={readyMap[blog.id] || null}
                onTogglePanel={() => togglePanel(blog.id)}
                onClosePanel={() => closePanel(blog.id)}
                onUpdateSetting={(key, value) => updateSetting(blog.id, key, value)}
                onGenerated={(articleId) => handleGenerated(blog.id, articleId)}
                onDismissReady={() => dismissReady(blog.id)}
                onTryAgain={() => { dismissReady(blog.id); togglePanel(blog.id); }}
                onOpenEditor={(articleId) => openEditor(articleId, blog.id)}
                onRatioChange={(val) => handleRatioChange(blog.id, val)}
              />
            ))}
          </div>

          <div className="blog-dashboard-snake">
            <SnakeGameMemo onGameOver={handleSnakeScore} />
            {snakeScore !== null && (
              <p className="blog-dashboard-snake-score">Last score: {snakeScore}</p>
            )}
          </div>

          <div className="blog-dashboard-tetris">
            <TetrisGameMemo onGameOver={handleTetrisScore} />
            {tetrisScore !== null && (
              <p className="blog-dashboard-tetris-score">Last score: {tetrisScore}</p>
            )}
          </div>
        </>
      )}

      {editorArticleId && (
        <ArticleEditorModal
          key={editorArticleId}
          articleId={editorArticleId}
          aspectRatio={editorAspectRatio}
          onClose={() => { setEditorArticleId(null); setEditorAspectRatio('16:9'); }}
        />
      )}
    </div>
  );
}

// ── BlogCard ───────────────────────────────────────────────

function BlogCard({ blog, panelOpen, settings, generating, readyArticleId, onTogglePanel, onClosePanel, onUpdateSetting, onGenerated, onDismissReady, onTryAgain, onOpenEditor, onRatioChange }) {
  const canStart = settings.topic.trim() !== '' || settings.promos.some((p) => p.trim() !== '');

  const isReady = !!readyArticleId || generating?.status === 'ready';
  const articleIdForEditor = readyArticleId || generating?.articleId || blog.last_article_id;
  const isError = generating?.status === 'error';
  const isGenerating = generating && !isReady && !isError;

  return (
    <div className={`blog-card${isGenerating ? ' generating' : ''}${isReady ? ' ready' : ''}`}>
      {/* Header row */}
      <div className="blog-card-header">
        <div className="blog-card-avatar">{initials(blog.name)}</div>
        <div className="blog-card-identity">
          <p className="blog-card-name">{blog.name}</p>
          <p className="blog-card-url">{blog.url || '—'}</p>
        </div>
      </div>

      {/* Meta row */}
      <div className="blog-card-meta">
        {blog.persona_name && (
          <>
            <span>{blog.persona_name}</span>
            <span className="blog-card-meta-sep">·</span>
          </>
        )}
        <span>{gsSummary(blog)}</span>
      </div>

      {/* Compact settings panel (inline expand) */}
      {panelOpen && (
        <CompactSettingsPanel
          settings={settings}
          canStart={canStart}
          blog={blog}
          onUpdate={onUpdateSetting}
          onCancel={onClosePanel}
          onGenerated={onGenerated}
          onRatioChange={onRatioChange}
        />
      )}

      {/* Action row */}
      <div className="blog-card-actions">
        <div>
          {isGenerating && (
            <span className="blog-card-generating-badge">
              <span className="spinner" style={{ width: 14, height: 14, border: '2px solid var(--color-border)', borderTopColor: 'var(--color-accent-secondary)', borderRadius: '50%', display: 'inline-block' }} />
              Generating…
            </span>
          )}
          {isReady && (
            <span className="blog-card-ready-badge">
              ✓ Ready
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 0 0 4px', fontSize: '0.75rem', lineHeight: 1 }}
                onClick={onDismissReady}
                title="Dismiss"
              >
                ✕
              </button>
            </span>
          )}
          {isError && (
            <div>
              <p className="blog-card-error-msg">{generating?.errorMsg || 'Generation failed.'}</p>
              <button className="btn-ghost btn-small" style={{ marginTop: '0.25rem' }} onClick={onTryAgain}>
                Try again
              </button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {!panelOpen && (
            <button className="btn-ghost btn-small" onClick={onTogglePanel}>
              Generate
            </button>
          )}
          {panelOpen && (
            <button className="btn-ghost btn-small" onClick={onClosePanel}>
              Close
            </button>
          )}
          {articleIdForEditor && !panelOpen && (
            <button
              className="btn-ghost btn-small"
              onClick={() => onOpenEditor(String(articleIdForEditor))}
            >
              Edit Article
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CompactSettingsPanel ───────────────────────────────────

function CompactSettingsPanel({ settings, canStart, blog, onUpdate, onCancel, onGenerated, onRatioChange }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const currentRatio = (blog.generation_settings || DEFAULT_GS).aspect_ratio || DEFAULT_ASPECT_RATIO;

  function updatePromo(idx, value) {
    const next = [...settings.promos];
    next[idx] = value;
    onUpdate('promos', next);
  }

  function addPromo() {
    onUpdate('promos', [...settings.promos, '']);
  }

  function removePromo(idx) {
    const next = settings.promos.filter((_, i) => i !== idx);
    onUpdate('promos', next.length > 0 ? next : ['']);
  }

  async function handleStart() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const gs = blog.generation_settings || DEFAULT_GS;
      const promos = settings.promos.map((p) => p.trim()).filter(Boolean);
      const payload = {
        blog_id: blog.id,
        ai_provider: gs.ai_provider || 'gemini',
        promos,
        topic: settings.topic.trim() || null,
        num_blocks: gs.num_blocks ?? 4,
        word_count_target: gs.word_count_target ?? 1200,
        num_images: gs.num_images ?? 4,
        num_real_photos: gs.num_real_photos ?? 0,
        promo_attachments: gs.promo_attachments || null,
        block_settings: gs.block_settings || null,
        research_settings: { ...(gs.research_settings || {}), news_selection_mode: 'auto' },
        aspect_ratio: gs.aspect_ratio || DEFAULT_ASPECT_RATIO,
      };
      const res = await generationAPI.generateArticle(payload);
      onGenerated(res.data.article_id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = 'Generation failed. Please try again.';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((d) => (typeof d === 'object' ? (d.msg || JSON.stringify(d)) : String(d))).join('; ');
      }
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="compact-panel">
      {/* Topic */}
      <div className="compact-panel-field">
        <label className="compact-panel-label">Topic <span style={{ fontWeight: 400, textTransform: 'none', opacity: 0.6 }}>(optional)</span></label>
        <input
          className="compact-panel-input"
          type="text"
          placeholder="e.g. Gold hits all-time high in March 2026"
          value={settings.topic}
          onChange={(e) => onUpdate('topic', e.target.value)}
        />
      </div>

      {/* Promos */}
      <div className="compact-panel-field">
        <label className="compact-panel-label">Promo blocks <span style={{ fontWeight: 400, textTransform: 'none', opacity: 0.6 }}>(optional)</span></label>
        {settings.promos.map((promo, idx) => (
          <div key={idx} className="compact-panel-promo-row">
            <textarea
              className="compact-panel-textarea"
              placeholder={`Promo ${idx + 1} — enter copy to embed in article…`}
              value={promo}
              onChange={(e) => updatePromo(idx, e.target.value)}
            />
            {settings.promos.length > 1 && (
              <button
                className="compact-panel-promo-remove"
                onClick={() => removePromo(idx)}
                title="Remove"
              >
                ✕
              </button>
            )}
          </div>
        ))}
        <button className="compact-panel-add-promo" onClick={addPromo}>
          + Add promo block
        </button>
      </div>

      <div className="compact-panel-field">
        <label className="compact-panel-label">Aspect Ratio</label>
        <div className="compact-ratio-grid">
          {ASPECT_RATIOS.map((r) => (
            <button
              key={r.value}
              type="button"
              className={`compact-ratio-btn${currentRatio === r.value ? ' selected' : ''}`}
              onClick={() => onRatioChange(r.value)}
            >
              {r.value}
            </button>
          ))}
        </div>
      </div>

      {submitError && (
        <p style={{ color: 'var(--color-error)', fontSize: '0.8rem', margin: 0 }}>{submitError}</p>
      )}
      <div className="compact-panel-footer">
        <button className="btn-ghost btn-small" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn-primary btn-small"
          onClick={handleStart}
          disabled={!canStart || submitting}
        >
          {submitting ? 'Starting…' : '▶ Start'}
        </button>
      </div>
    </div>
  );
}

export default BlogDashboard;
